<template>
    <div class="practice-pulse-details-page">
      <PopupErrorDisplay/>

      <!-- ======================= RECALL SECTION ======================= -->
      <div v-if="showRecall" class="details-panel">
        <div class="panel-header">
          <div class="header-content">
            <i class="fa fa-calendar-check-o header-icon"></i>
            <h1 class="header-title">Recall Details</h1>
          </div>
          <button type="button" class="btn-close-panel" @click="closeSlideOutPanel" v-b-tooltip.hover title="Close">
            <i class="la la-times"></i>
          </button>
        </div>

        <div class="panel-body">
          <div class="data-section">
            <div class="table-responsive">
              <v-client-table v-if="recallData" :data="recallData" :columns="recallColumns" :options="options">
                <div slot="h__Name">Name</div>
                <div slot="h__RecallScheduled">Recall Scheduled</div>
                <div slot="h__RecallDue">Recall Due</div>

                <div slot="Name" slot-scope="props">
                  <a href="#" class="patient-link" @click="gotoPatientPage(props.row.PatNum)">{{props.row.LName}}, {{props.row.FName}}</a>
                </div>

                <div slot="RecallScheduled" slot-scope="props">
                    <span v-if="props.row.AptDateTime === null" class="text-muted">Not Scheduled</span>
                    <span v-else>{{ props.row.AptDateTime | dateformat(dateFormatStr)}}</span>
                </div>
                <div slot="RecallDue" slot-scope="props">
                  <span v-if="props.row.RecallDue === null"></span>
                  <span v-else>{{ props.row.RecallDue | dateformat(dateFormatStr)}}</span>
                </div>
              </v-client-table>
            </div>
          </div>
        </div>
      </div>

      <!-- ======================= PATIENT BALANCE SECTION ======================= -->
      <div v-else-if="showPatientBalance" class="details-panel">
        <div class="panel-header">
          <div class="header-content">
            <i class="fa fa-dollar header-icon"></i>
            <h1 class="header-title">Patient Balance Details</h1>
          </div>
          <button type="button" class="btn-close-panel" @click="closeSlideOutPanel" v-b-tooltip.hover title="Close">
            <i class="la la-times"></i>
          </button>
        </div>

        <div class="panel-body">
          <div class="data-section">
            <div class="table-responsive">
              <v-client-table v-if="patientBalanceData" :data="patientBalanceData" :columns="patientBalanceColumns" :options="options">
                <div slot="h__Guarantor">Guarantor</div>
                <div slot="h__PatientAmount">Balance</div>

                <div slot="Name" slot-scope="props">
                  <a href="#" class="patient-link" @click="gotoPatientPage(props.row.PatNum)">{{props.row.Guarantor}}</a>
                </div>

                <div slot="PatientAmount" slot-scope="props">
                  {{ props.row.PatientAmount | money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue) }}
                </div>
              </v-client-table>
            </div>
          </div>
        </div>
      </div>

      <!-- ======================= TASKS SECTION ======================= -->
      <div v-else-if="showTasks" class="details-panel">
        <div class="panel-header">
          <div class="header-content">
            <i class="fa fa-tasks header-icon"></i>
            <h1 class="header-title">Patient Tasks</h1>
          </div>
          <button type="button" class="btn-close-panel" @click="closeSlideOutPanel" v-b-tooltip.hover title="Close">
            <i class="la la-times"></i>
          </button>
        </div>

        <div class="panel-body">
          <div class="data-section">
            <div class="task-input-section">
              <at at="@" :members="activeEmployees" name-key="Name" v-model="newTodoContent" v-if="employees" ref="empat">
                <template slot="item" slot-scope="s">
                  <span tabindex="-1">{{s.item.Name}}</span>
                </template>
                <template slot="embeddedItem" slot-scope="s">
                  <span>
                      <span tabindex="-1" :data-teamio-employeeid="s.current.EmployeeID" class="badge mr-xs badge-primary badge-pill mt-1">{{s.current.Name + " "}}</span>
                    </span>
                </template>
                <div class="ToDo__addNew newtodo"
                  id="newtodo"
                  ref="newtodo"
                  placeholder="Add your task here...(use @ to assign the task to someone or # to tag a patient)"
                  contenteditable
                  />
              </at>
              <div class="task-actions">
                <b-button variant="success" type="submit" class="btn-add-task" @click="saveTodo">
                  <i class="fa fa-plus mr-1"></i> Add Task
                </b-button>
                <b-button variant="outline-secondary" type="submit" class="btn-cancel" @click="closeSlideOutPanel">Cancel</b-button>
              </div>
            </div>

            <div class="task-list-section">
              <todo-item
                v-for="(item, index) in taggedTasks[detailData.patNum]"
                :key="item.key"
                :item="item"
                :moreThanOneItems="taggedTasks[detailData.patNum].length > 1"
                :isLoading=false
                @change="handleItemChange"
                @edit="editItem($event, index, null)"
                @delete="deleteTaskItem"
                @deleteThisInstanceOnly="deleteThisInstanceOnly"
              />
            </div>
          </div>
        </div>
        <slideout-panel ref="taskdetailspage"></slideout-panel>
      </div>

      <!-- ======================= ORTHO SECTION ======================= -->
      <div v-else-if="showOrtho" class="details-panel">
        <div class="panel-header">
          <div class="header-content">
            <i class="fa fa-smile-o header-icon"></i>
            <h1 class="header-title">Ortho Details</h1>
          </div>
          <button type="button" class="btn-close-panel" @click="closeSlideOutPanel" v-b-tooltip.hover title="Close">
            <i class="la la-times"></i>
          </button>
        </div>

        <div class="panel-body">
          <div class="data-section">
            <div class="table-responsive">
              <v-client-table v-if="orthoData" :data="orthoData" :columns="orthoColumns" :options="options">
                <div slot="h__Patient">Patient</div>
                <div slot="h__Age">Age</div>
                <div slot="h__OrthoCoverage">Ortho Coverage</div>
                <div slot="h__AgeLimit">Age Limit</div>
                <div slot="h__OrthoMax">Ortho Max</div>
                <div slot="h__OpName">Operatory</div>
                <div slot="Patient" slot-scope="props">
                  <a href="#" class="patient-link" @click="gotoPatientPage(props.row.PatNum)">{{props.row.Patient}}</a>
                </div>

                <div slot="OrthoCoverage" slot-scope="props">
                  <span class="status-badge status-success" v-if="props.row.AgeLimit !== null && parseInt(props.row.Age, 10) <= props.row.AgeLimit">
                    <i class="fa fa-check"></i> Yes
                  </span>
                  <span v-if="props.row.OrthoCoverage === 'NO'" class="status-badge status-muted">No</span>
                </div>

                <div slot="OrthoMax" slot-scope="props">
                  {{props.row.OrthoMax | money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue)}}
                </div>
              </v-client-table>
            </div>
          </div>
        </div>
      </div>

      <!-- ======================= REPORT SECTION ======================= -->
      <div v-else-if="showReport" class="details-panel">
        <div class="panel-header">
          <div class="header-content">
            <i class="fa fa-bar-chart header-icon"></i>
            <h1 class="header-title">Patient Flow Report</h1>
          </div>
          <button type="button" class="btn-close-panel" @click="closeSlideOutPanel" v-b-tooltip.hover title="Close">
            <i class="la la-times"></i>
          </button>
        </div>

        <div class="panel-body">
          <div class="data-section">
            <div class="report-filters">
              <div class="filter-group">
                <label class="filter-label">Select Date</label>
                <DatePicker
                  lang="en"
                  placeholder="Select date..."
                  input-class="form-control date-input"
                  v-model="reportDate"
                  format="MM-DD-YYYY"
                  class="date-picker-wrap"
                  append-to-body
                >
                  <i class="fa fa-calendar" slot="calendar-icon" />
                </DatePicker>
              </div>
              <div class="filter-actions">
                <b-button variant="success" class="btn-view-report" @click="viewReport">
                  <i class="fa fa-eye mr-1"></i> View Report
                </b-button>
              </div>
            </div>

            <div class="table-responsive">
              <v-client-table class="report-table" v-if="reportData" :data="reportData" :columns="reportColumns" :options="options">
                <div slot="h__PatNum">Patient</div>
                <div slot="h__AptDateTime">Appt Time</div>
                <div slot="h__Arrived">Arrived</div>
                <div slot="h__Ready">Ready</div>
                <div slot="h__TreatmentStarted">Treatment Started</div>
                <div slot="h__Checkout">Checkout Time</div>
                <div slot="h__Dismissed">Dismissed Time</div>
                <div slot="h__WaitTime">Wait Time (min)</div>
                <div slot="h__TreatmentTime">Treatment Time (min)</div>
                <div slot="h__SendEmail">Post-op Call</div>

                <div slot="PatNum" slot-scope="props">
                  <span class="patient-name">{{getPatientName(props.row.PatNum)}}</span>
                </div>

                <div slot="AptDateTime" slot-scope="props">
                  <span class="time-value">{{ formatPPTime(props.row.AptDateTime) }}</span>
                </div>

                <div slot="Arrived" slot-scope="props">
                  <span class="time-value">{{ formatPPTime(props.row.Arrived) }}</span>
                </div>

                <div slot="Ready" slot-scope="props">
                  <span class="time-value">{{ formatPPTime(props.row.Ready) }}</span>
                </div>

                <div slot="TreatmentStarted" slot-scope="props">
                  <span class="time-value">{{ formatPPTime(props.row.TreatmentStarted) }}</span>
                </div>

                <div slot="Checkout" slot-scope="props">
                  <span class="time-value">{{ formatPPTime(props.row.Checkout) }}</span>
                </div>

                <div slot="Dismissed" slot-scope="props">
                  <span class="time-value">{{ formatPPTime(props.row.Dismissed) }}</span>
                </div>

                <div slot="WaitTime" slot-scope="props">
                  <span class="time-value" :class="{ 'high-time': getWaitTime(props.row) > (detailData.practicePulseSettings ? detailData.practicePulseSettings.elapsedTime : 999) }">
                    {{ getWaitTime(props.row) }}
                  </span>
                </div>

                <div slot="TreatmentTime" slot-scope="props">
                  <span class="time-value">{{ getTreatmentTime(props.row) }}</span>
                </div>

                <div slot="SendEmail" slot-scope="props">
                  <span v-if="props.row.SendEmail === 'TRUE'" class="status-badge status-success">
                    <i class="fa fa-check"></i>
                  </span>
                </div>

              </v-client-table>
            </div>
          </div>
        </div>
      </div>

      <!-- ======================= WIKI SECTION ======================= -->
      <div v-else-if="showWiki" class="details-panel">
        <div class="panel-header">
          <div class="header-content">
            <i class="fa fa-book header-icon"></i>
            <h1 class="header-title">{{ this.wikiPage[0][0].Name}}</h1>
          </div>
          <button type="button" class="btn-close-panel" @click="closeSlideOutPanel" v-b-tooltip.hover title="Close">
            <i class="la la-times"></i>
          </button>
        </div>

        <div class="panel-body">
          <div class="wiki-content-section">
            <CustomEditor
              v-if="this.wikiPage && this.wikiPage.length > 0"
              :editorData="this.editorData"
              :editorTools="editorTools"
              :viewMode=true
              @save="save"
              @onReady="onReady"
            >
            </CustomEditor>
          </div>
        </div>
      </div>

  </div>
</template>

<script>
/* eslint-disable */
// https://github.com/codex-team/editor.js/issues/676

import Multiselect from "vue-multiselect";
//import { Editor } from 'vue-editor-js'
import PopupErrorDisplay from "@/components/ErrorDisplay/PopupErrorDisplay";
import CustomEditor from "@/components/Editor/Editor.vue";
import { mapGetters, mapState, mapActions, mapMutations } from 'vuex';
import TodoItem from '../TaskList/components/TodoItem';
import TodoDetails from '../TaskList/components/TodoDetails';
import _ from 'lodash';
import Widget from '@/components/Widget/Widget';
import At from '../TaskList/vueat/At';
import moment from 'moment-timezone';
import DatePicker from "vue2-datepicker";
import mixin from '../../mixin.js';

  export default {
    name: 'ShowPracticePulseDetails',
    components: { Multiselect, DatePicker, CustomEditor, PopupErrorDisplay, Widget, TodoItem, TodoDetails, At },
    props: ['detailType', 'detailData'],
    mixins: [mixin],

    mounted() {
      if(this.showTasks)
        this.setCaretAtBeginning();
    },

    created() {
      this.init();
    },

    data() {
      return {
        newTodoContent: '',
        editInProgress: false,
        wikiPages: [],
        wikiPage: {},
        selectedTags: [],
        taggedTasks: {},  // object with keys of PatNum that has all the tagged tasks for this patient. This was to make it easy to watch tasks and update this
        recallColumns: ['Name', 'RecallScheduled', 'RecallDue'],
        orthoColumns: ['Patient', 'Age', 'OrthoCoverage', 'AgeLimit', 'OrthoMax', 'OpName'],
        patientBalanceColumns: ['Guarantor', 'PatientAmount'],
        reportColumns: ['PatNum', 'AptDateTime', 'Arrived', 'Ready', 'TreatmentStarted', 'Checkout', 'Dismissed', 'WaitTime', 'TreatmentTime', 'SendEmail'],
        reportDate: moment(),        
        reportData: [],
        options: {
          filterable: [],
          skin:'table',          
          sortByColumn: true,
          sortable: ['PatNum', 'AptDateTime', 'Arrived', 'Ready', 'TreatmentStarted', 'Checkout', 'Dismissed', 'WaitTime', 'TreatmentTime'],
          perPage: 10,
          sortByColumn: false,
          pagination: { chunk: 10, dropdown: false },
          rowAttributesCallback: row => {            
            let time = this.getWaitTime(row);
            //let time = this.getTreatmentTime(row); 
            if(this.detailData && this.detailData.practicePulseSettings && this.detailData.practicePulseSettings.elapsedTime) {
              if(time > this.detailData.practicePulseSettings.elapsedTime)
                return {"style": "background-color: #f55d5d8a; "};
            }
          },
          cellClasses:{
            WaitTime: [
              {
                  class:'hightime',
                  condition: row => this.getWaitTime(row) > this.detailData.practicePulseSettings.elapsedTime
              }
            ]
          }
        },
        editorTools: {
          header: {
            class: "Header",
            inlineToolbar: ["marker", "italic", "bold", "link"]
          },
          simpleImage: { class: "SimpleImage" },
          image: {
            class: "ImageTool",
          },
          embed: {
            class: "Embed",
            inlineToolbar: true
          },
          list: {
            class: "List",
            inlineToolbar: true
          },
          delimiter: { class: "Delimiter" },
          table: {
            class: "Table",
            inlineToolbar: true
          },
          marker: {
            class: "Marker",
            shortcut: "CMD+SHIFT+M"
          },
          inlineCode: {
            class: "InlineCode",
            shortcut: "CMD+SHIFT+C"
          },
          paragraph: {
            class: "Paragraph",
            inlineToolbar: true
          },
          checklist: {
          class: "Checklist",
          inlineToolbar: true
          },
          wikiLink: {
            class: "wikiLink",
            inlineToolbar: true
          }
        },
        value: [],
        simpleDropdownVariant: "Select",
        isPickerActive: false
      };
  },

  methods: {
      ...mapActions('alert', ['setDismissSuccessCountDown', 'setErrored', 'setMessage']),      
      ...mapActions('tasks', ['getTasksForEmployee', 'getTasksForSite', 'getTaskDetails', 'addPersonalTask', 'updateTask', 'updateMultipleTasks', 'deleteTask', 'deleteThisInstanceOfRepeatedTask']),
      ...mapActions('practicepulse', ['getPracticePulseReport']),

      async init() {        
        if(this.showReport) {
          this.viewReport();
        }
      },

      async viewReport(d) {        
        let payload = {
            siteID: this.siteID,            
            _date: moment(this.reportDate).format('YYYY-MM-DD')
          }
          this.reportData = await this.getPracticePulseReport(payload);
      },

      gotoPatientPage(patNum) {
        let teamioPatientID = `${this.siteID}-${patNum}`;
        let routeData = this.$router.resolve({name: 'patientprofile', params: {patientID:  teamioPatientID}});
        window.open(routeData.href, '_blank');
      },

      setCaretAtBeginning() {
        var el = document.getElementById("newtodo");
        if(el !== null) {
          var range = document.createRange();
          var sel = window.getSelection();
          range.setStart(el, 0);
          range.collapse(true);
          sel.removeAllRanges();
          sel.addRange(range);
          el.focus();
        }
      },

      async closeSlideOutPanel() {          
        this.$emit('closePanel',this.goals);
      },

      async save() {            
          this.$emit('closePanel',this.goals);
      },

      onReady: () => {
        var elements = document.querySelectorAll('[contenteditable=true]');
        elements.forEach(element => {
          element.setAttribute('contenteditable' , false);
        });
      },
      
      async handleItemChange (item) {
        //console.log(`handleItemChange called with item: ${JSON.stringify(item)}`);
        await this.updateTask(item);
      },

      async deleteTaskItem(item) {
        //console.log(`trying to delete item: ${JSON.stringify(item)}`);
        await this.deleteTask({
          id: item.id,
          deleted: true,
          deletedBy: this.userID,
          deletedOn: moment().toDate(),
        });
      },

      async deleteThisInstanceOnly(item) {
        let payload = {
          task : item,
          clientDateFormat : this.detailData.allSettings.GENERAL_TIMEZONE.PropertyValue.toUpperCase() === 'AFRICA/MASERU' ? 'DD/MM/YYYY' : 'MM/DD/YYYY'
        }
        //console.log(`payload: ${JSON.stringify(payload)}`);
        await this.deleteThisInstanceOfRepeatedTask(payload);
      },

      getLinkedEmployeeID(content) {
        let regex = /data-teamio-employeeid="([a-zA-z0-9\-]+).*?/g;
        let result = regex.exec(content);
        let result1 = content.match(regex);
        //console.dir(result1);
        //console.log(`result1: ${JSON.stringify(result1, null, ' ')}`);

        if(result1 && result1.length > 1) {
          // User has @mentioned multiple people. Warn them that they can only assign the task to 1 person
          throw new Error("You tried to assign the task to more than one person. You can only @mention at max one person");
        }

        if(result) {
            //console.log(`result: ${JSON.stringify(result, null, ' ')}`);
            return result[1];
        } else
            return null;
      },

      getLinkedWikiPageID(content) {
          let regex = /data-teamio-wikipageid="([a-zA-z0-9\-]+).*/;
          let result = regex.exec(content);
          if(result) {
              //console.log(`regex result: ${result}`);
              return result[1];
          } else
              return null;
      },

      getTaggedPatientIDs(content) {
        //let regex = /data-teamio-patientid="([a-zA-z0-9\-]+).*/;
        let regex = /data-teamio-patientid=\"([0-9]+)\.?/;
        let m;
        let ret = [];

        while((m = regex.exec(content)) !== null) {
          //console.log(m);
          if(m.length > 0) {
            ret.push(parseInt(m[1],10));
            content = content.substring(m.index+1); 
          }
        }
        return ret;
      },

      ensurePatientTagged(content) {
        let taggedPatients = this.getTaggedPatientIDs(content);
        if(!taggedPatients.includes(this.detailData.patNum)) {
          //console.log('current patnum not tagged. now tagging');
          let patient = this.getPatient(this.detailData.patNum);
          //console.log('patient', patient);
          let html = `&nbsp; <span data-at-embedded="" contenteditable="false"> <span data-v-422f84db="" class="badge mr-xs badge-secondary badge-pill mt-1" data-teamio-patientid="${this.detailData.patNum}"><a data-v-422f84db="" href="/app/patientprofile/${this.detailData.siteID}-${this.detailData.patNum}" class="">${patient.LName},${patient.FName}</a></span> </span>`;
          return content = content + html;
        }
      },

      // remove the tag of who this task is assigned to when saving
      removedAssignedTo(content) {
        let regex = /<span data-at-embedded="" contenteditable="false"> <span class="badge mr-xs badge-primary.*data-teamio-employeeid="([a-zA-z0-9\-]+).*?<\/span>\s+<\/span>/;
        let temp = content.replace(new RegExp(regex, "gm"), '');
        return temp;
      },

      async opentaskdetails(taskID) {
        //console.log(`Got ${taskID} in opentaskdetails`);
        try {
          let task = await this.getTaskDetails({taskID: taskID});
          //console.log(`task: ${JSON.stringify(task)}`);
          this.editInProgress = true;

          const panel1Handle = this.$showPanel({
            component : TodoDetails,
            openOn: 'right',
            width: 750,
            props: {
              task: task,
              wikiPages: this.wikiPages,
              patients: this.patients,
            }
          })

          panel1Handle.promise
            .then(result => {
              //console.log(`panel handle promise done`);
              this.editInProgress = false;
          });
        } catch(error) {
          this.editInProgress = false;
          console.log(`Error while trying to edit task: ${error}`);
          panel1Handle.hide();
        }
      },

      async editItem(item, index, taskType) {
        let task;
        try {
          task = await this.getTaskDetails({taskID: item.id});
          //console.log(`task: ${JSON.stringify(task)}`);  

          const panel1Handle = this.$showPanel({
            component : TodoDetails,
            openOn: 'right',
            width: 750,
            props: {
              task: task,
              wikiPages: this.wikiPages,
              patients: this.patients,
            }
          })

          panel1Handle.promise
            .then(result => {
              //console.log(`panel handle promise done`, result);
              //Vue.set(temp, index, false);
          });
        } catch(error) {
          console.log(`Error while trying to edit task: ${error}`);
          //Vue.set(temp, index, true);
        }
      },

      async saveTodo () {
        /*
        console.log(`innerText: ${this.$refs['newtodo'].innerText}`);
        console.log(`innerHtml: ${this.$refs['newtodo'].innerHTML}`);
        console.log(`textContent: ${this.$refs['newtodo'].textContent}`);
        console.log(`i am in save: ${this.$refs['newtodo']}`);
        */

        let content = this.$refs['newtodo'].innerText;
        let contentHTML = this.$refs['newtodo'].innerHTML;

        // do nothing if we have no content
        if (!content) return;
        
        //console.log(`content: ${content}`);
        //console.log(`contentHTML: ${contentHTML}`); 
        
        let assignedToEmpID;

        try {
          assignedToEmpID = this.getLinkedEmployeeID(contentHTML);
          //console.log(`Assigned to: ${assignedToEmpID}`);
          contentHTML = this.removedAssignedTo(contentHTML);
          //console.log(`after removal: ${contentHTML}`);
        } catch(err) {
          console.log(err);          
          this.$toast.error('You can only @mention ONE person who will be assigned this task. Please fix this and try again');
          return;
        }

        contentHTML = this.ensurePatientTagged(contentHTML);

        if(!contentHTML || contentHTML === '') {          
          this.$toast.error('Please enter a task description before submitting');
          return;
        }          

        assignedToEmpID = assignedToEmpID ? assignedToEmpID : this.userID;
        //console.log(`max: ${this.maxSortID}`);
        this.maxSortID = _.max(_.map(this.todoItems, 'sortID'));
        //console.log(`max: ${this.maxSortID}`);

        let task = {
          siteID: this.siteID,
          status: 'INCOMPLETE',
          //id: Math.random().toString(36).substr(2, 5),
          sortID: this.maxSortID ? this.maxSortID + 1 : 1,
          content: contentHTML,
          createdAt: moment().toDate(),
          dueAt: moment().toDate(),
          dueAtStr: moment().format('MM/DD/YYYY'),
          assignedToEmpID: assignedToEmpID,
          createdByEmpID: this.userID,
          timestamp: new Date().getTime(),
          timezone: this.detailData.allSettings.GENERAL_TIMEZONE.PropertyValue,
          lastActivity: moment().toDate(), // this will have the date when EITHER task or any comments on the task was updated
          deleted: false
        }

        let taggedPatients = this.getTaggedPatientIDs(contentHTML);
        if(taggedPatients.length > 0)
          task['taggedPatients'] = taggedPatients;

        let notification;

        if(assignedToEmpID !== this.userID) {
          notification = {
            audience: 'EMPLOYEE',
            employeeID: assignedToEmpID,
            type: 'TASK',
            text: `You have been assigned a <a href='/app/todo/#taskID#'>task</a> by ${this.detailData.user.Name}`,    // #taskID# will be replaced with the actual ID of the task
            siteID: this.siteID,
            createdAt: new Date(),
            unreadList: new Array(assignedToEmpID)
          };
        }

        let payload = {
          task,
          ...(notification && { notification: notification }),
        }

        //console.log(`Task payload: ${JSON.stringify(payload, null, ' ')}`);

        try {          
          this.$refs['newtodo'].innerHTML = '';
          // store the items locally in local storage
          //this.storeItems();
          // store items in firebase
          await this.addPersonalTask(payload);
          this.$nextTick(() => this.$refs.newtodo.focus());
          this.setCaretAtBeginning();
        } catch(err) {
          //await this.deleteTask({id: task.id});
          console.error(err);
        }
      },

      convertFromFBDateToJSDate(d) {
        if(d && d !== null)
          return moment(d.toDate()).format('MM-DD-YYYY hh:mma');
      },


      getPatientName(patNum) {
        let p = this.getPatient(patNum);
        return `${p.FName} ${p.LName}`;
      },

      getWaitTime(patient) {
        //console.log('in getWiatTime, patient', patient);
        let waitTime;
        let arrived = patient.Arrived === null ? moment(patient.Ready) : moment(patient.Arrived);
        let aptDateTime = moment(patient.AptDateTime);
        let treatmentStartedTime = moment(patient.TreatmentStarted);

        if(arrived.diff(aptDateTime, 'hours') >= 0) {
          // arrived is bigger
          waitTime = treatmentStartedTime.diff(arrived, 'minutes');
        } else {
          waitTime = treatmentStartedTime.diff(aptDateTime, 'minutes');
        }
        return waitTime;
      },

      getTreatmentTime(patient) {        
        //console.log('in getTreatmentTime, patient', patient);
        let treatmentTime;
        let checkedoutTime = moment(patient.Checkout);
        let treatmentStartedTime = moment(patient.TreatmentStarted);
        treatmentTime = checkedoutTime.diff(treatmentStartedTime, 'minutes');
        console.log('treatmentTime', treatmentTime);
        if(!Number.isNaN(treatmentTime))
          return treatmentTime;
        else
          return '';
      },

      formatPPTime(d) {
        //console.log('in formatPPTime', d);
        if(d !== null) {
          let temp = moment(d).tz(this.allSettings.GENERAL_TIMEZONE.PropertyValue);        
          return temp.format('h:mm a');        
        } else
          return '';
      }
    },

    computed: {
      ...mapGetters({      
        'activeEmployees': 'customer/activeEmployees',       
        'getPatient': 'customer/getPatient',        
      }),

      ...mapState({
          loading: state => state.alert.loading,
          loaded: state => state.alert.loaded,
          errored: state => state.alert.errored,
          alertMessage: state => state.alert.message,
          dismissSuccessCountDown : state => state.alert.dismissSuccessCountDown,
          siteID: state => state.customer.siteID,
          userID: state => state.customer.userID,
          sites: state => state.customer.sites,
          employees: state => state.customer.employees,
          patients: state => state.customer.patients,
          tasksTaggedWithPatients: state => state.tasks.tasksTaggedWithPatients,
      }),

      showWiki() {
        if(this.detailType === 'wiki')
          return true;
        else
          return false;
      },

      showTasks() {
        if(this.detailType === 'tasks')
          return true;
        else
          return false;
      },

      showRecall() {
        if(this.detailType === 'recall')
          return true;
        else
          return false;
      },

      showPatientBalance() {
        if(this.detailType === 'balance')
          return true;
        else
          return false;
      },

      showReport() {
        if(this.detailType === 'report')
          return true;
        else
          return false;
      },

      showOrtho() {
        if(this.detailType === 'ortho')
          return true;
        else
          return false;
      },

      recallData() {
        if(this.detailData && this.detailData.patientData)
          return this.detailData.patientData.recallResults;
      },

      orthoData() {
        if(this.detailData && this.detailData.patientData)
          return this.detailData.patientData.orthoResults;
      },

      patientBalanceData() {
        if(this.detailData && this.detailData.patientData)
          return this.detailData.patientData.patientBalanceResults;
      },

      editorData() {
        if(this.wikiPage && this.wikiPage.length > 0) {
          // load the selected tags
          let x = [];
          let wikiTags;
          let _siteTags = this.siteTags;     
          //console.log('_siteTags', _siteTags);
          if(this.wikiPage && this.wikiPage.length > 0 && this.wikiPage[0][0])
            wikiTags = this.wikiPage[0][0].Tags;
          if(wikiTags) {
            //console.log('wikiTags', wikiTags);
            let tagArray = wikiTags.split(',');
            tagArray.forEach(function(tag) {
              let o = {};
              let tagIDObj = _.find(_siteTags, {'Tag': tag});
              let tagID;
              if(tagIDObj)
                tagID = tagIDObj.TagID;
              if(!tagID)
                tagID = '';
              o.TagID = tagID,
              o.Tag = tag
              x.push(o);
            });
          }
          this.selectedTags = x;
          return JSON.parse(this.wikiPage[0][0].Content);
        }
        else
          return {};
      },

      siteTags() {
        if(this.wikiPage && this.wikiPage.length > 1)
          return this.wikiPage[1];
      },
    },

    watch: {
      tasksTaggedWithPatients: {
          immediate: true,
          handler(newValue, oldValue) {   
          //console.log('in showpp details, watch change tasks tagged with patients', newValue, oldValue);
          let _this = this;
          _this.taggedTasks = {};        
          for(const task of newValue) {
            if(task.taggedPatients && task.taggedPatients.length > 0) {
              let taggedPatients = task.taggedPatients;            
              for(const patNum of taggedPatients) {              
                if(Array.isArray(_this.taggedTasks[patNum]))
                  _this.taggedTasks[patNum].push(task);
                else {
                  _this.taggedTasks[patNum] = [];
                  _this.taggedTasks[patNum].push(task);
                }
              }
            }
          }
        }
      }
    }
  };
</script>

<style scoped lang="scss">

.dot-before {
    margin: 3px 0;

    &::before {
      content: '•';
      margin-right: 7.5px;
    }
}

li {
  color: #3abf94;
  font-weight: bold;
}

.example {
  color: #000000;
  font-weight: normal;
  margin-left: 15px;
}

.ToDo {
  &__items {
    margin-top: 1.5rem;
  }

  &__assignedToMe {
    margin-top: 20px
  }
}


:active :focus {
  border-color: #aba3ff;
}

.newtodo {
  width: 100%;
  padding: 5px 5px;
  font-weight: 400;
  //border: 1px solid grey ;
  background-color: #fff;
  border: 2px solid #e0e6e8;
  border-radius: 3px;
  line-height: normal;
  margin: 0;
  //padding: 0 0 0 16px;
  transition: border .15s;
  cursor: auto;
}

[contenteditable][placeholder]:empty:before {
  content: attr(placeholder);
  position: absolute;
  color: lightgray;
  background-color: transparent;
}

*[contenteditable="true"]{display: inline-block;}

.tooltip-inner {
  padding: -10px;
}



</style>

<style src="./ppdetailsstyles.scss" lang="scss" />

